import "./styles.scss";

import Dialog from "@material-ui/core/Dialog";
import { useTranslation } from "react-i18next";
import { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import Select from "../Checkout/Select";
import Inputs from "../Checkout/Inputs";
import { clearQuestionnaires, setPendingTopicsQuestionnaires } from "../../store/slices/questionnaires.slice";
import { getPendingQuestionnaires, getQuestionnairesById, postAnswerQuestionnaires } from "../../store/middleware";

const formatNativeLanguageName = { Spanish: "es", English: "en", Portuguese: "pt", French: "fr" };

function ModalQuestionnaires({ showModal, setShowModal }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.userReducer);
  const { pendingTopicsQuestionnaires, questionnairesById } = useSelector((state) => state.questionnairesReducer);

  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const nativeLanguageName = formatNativeLanguageName[user.nativeLanguageName];
  const filteredTopicsNotAnswered = Array.isArray(pendingTopicsQuestionnaires)
    ? pendingTopicsQuestionnaires.filter((item) => !item.answered)
    : [];

  const currentQuestionnaire = filteredTopicsNotAnswered[0];
  const currentQuestions = questionnairesById[0]?.questions || [];
  const totalPending = filteredTopicsNotAnswered.length;

  const totalQuestionnaires = Array.isArray(pendingTopicsQuestionnaires) ? pendingTopicsQuestionnaires.length : 0;
  const answeredCount = totalQuestionnaires - totalPending;
  const currentStep = answeredCount + 1;

  const getData = async (questionnaireId) => {
    if (!questionnaireId) return;
    setLoading(true);
    await dispatch(getQuestionnairesById(questionnaireId));
    setLoading(false);
  };

  const closeModal = () => {
    setForm({});
    setSuccess(false);
    setError("");
    setShowModal(false);
    dispatch(clearQuestionnaires());
  };

  const handleBackStep = () => {
    closeModal();
  };

  const handleNextStep = async () => {
    const responseSave = await handleSave();
    if (!responseSave) return;

    const remainingQuestionnaires = filteredTopicsNotAnswered.filter(
      (item) => item.questionnaireId !== currentQuestionnaire?.questionnaireId,
    );

    if (remainingQuestionnaires.length === 0) {
      setSuccess(true);
    } else {
      setForm({});
      setError("");

      await getData(remainingQuestionnaires[0]?.questionnaireId);
    }
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    setForm((state) => ({ ...state, [name]: value }));
    setError("");
  };

  const handleSave = async () => {
    setError("");
    setSending(true);

    const cleanedForm = Object.fromEntries(
      Object.entries(form).filter(([, value]) => {
        if (typeof value === "string") return value.trim() !== "";
        return value !== null && value !== undefined;
      }),
    );

    if (Object.keys(cleanedForm).length !== currentQuestions.length) {
      setError(t("INVALID_FORM_DESCRIPTION"));
      setSending(false);
      return false;
    }

    const answers = Object.keys(form).map((key) => ({
      question_id: key,
      answer: form[key],
    }));

    const payload = {
      answers,
      responseLanguage: nativeLanguageName,
      questionnaireId: currentQuestionnaire?.questionnaireId,
    };

    try {
      const response = await dispatch(postAnswerQuestionnaires(payload));
      const responsePendingTopics = await dispatch(getPendingQuestionnaires());

      dispatch(setPendingTopicsQuestionnaires(responsePendingTopics));
      setSending(false);
      return response;
    } catch (err) {
      setError(t("ERROR_SAVING"));
      setSending(false);
      return false;
    }
  };

  useEffect(() => {
    if (showModal && currentQuestionnaire?.questionnaireId) {
      getData(currentQuestionnaire.questionnaireId);
    }
    // eslint-disable-next-line
  }, [showModal, filteredTopicsNotAnswered.length]);

  if (!currentQuestionnaire) return null;

  return (
    <Dialog
      open={showModal}
      classes="modalQuestionnaires"
      className="modalQuestionnaires"
      onClose={() => setShowModal(false)}
    >
      <div className="containerQuestionnaires">
        <h3 className="title">{t("QUESTIONNAIRES")}</h3>
        <h4 className="messageAlert">{t("MESSAGE_ALERT_QUESTIONNAIRES")}</h4>

        {!success ? (
          <>
            <form className="contentQuestionnaires">
              <div className="headerTitle">
                <p style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>
                  {currentStep} of {totalQuestionnaires}
                </p>
                <h4 className="subTitle">{currentQuestionnaire?.titleTranslations[nativeLanguageName]}</h4>
              </div>

              {error && (
                <div className="error-message" style={{ color: "red", marginBottom: 15 }}>
                  {error}
                </div>
              )}

              {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <p>{t("LOADING")}</p>
                </div>
              ) : currentQuestions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <p>{t("NO_QUESTIONS_AVAILABLE")}</p>
                </div>
              ) : (
                currentQuestions.map((item) =>
                  item.type === "multiple_choice" ? (
                    <Select
                      key={item.id}
                      name={item.id}
                      value={form[item.id] || ""}
                      onChange={handleChange}
                      className="wrapperSelect"
                      style={{ marginBottom: 13 }}
                      label={item.translations[nativeLanguageName]}
                      options={item.options[nativeLanguageName].map((option) => ({
                        id: option,
                        name: option,
                      }))}
                    />
                  ) : (
                    <Inputs
                      key={item.id}
                      name={item.id}
                      type={item.type}
                      value={form[item.id] || ""}
                      onChange={handleChange}
                      style={{ marginBottom: 35 }}
                      label={item.translations[nativeLanguageName]}
                    />
                  ),
                )
              )}
            </form>
          </>
        ) : (
          <h4 className="textAlign">{t("SUCCESS_MESSAGE_TAX_INFORMATION")}</h4>
        )}

        <div className="footer">
          {!success ? (
            <Fragment>
              <button className="buttonModalBack" onClick={handleBackStep} disabled={loading || sending}>
                {t("BACK")}
              </button>
              <button
                disabled={sending || loading || currentQuestions.length === 0}
                className="buttonModalSave"
                onClick={handleNextStep}
              >
                {sending ? "Salvando..." : totalPending === 1 ? t("SAVE") : t("BTN_NEXT")}
              </button>
            </Fragment>
          ) : (
            <button className="buttonModalSave" onClick={closeModal}>
              {t("BTN_CLOSE")}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export default ModalQuestionnaires;
